import React from 'react'
import {
  type GridAutosizeOptions,
  type GridColDef,
  type GridSortModel,
} from '@mui/x-data-grid-pro'
import { Box } from '@mui/material'
import clsx from 'clsx'
import { formatRawUsdAmount, roundCoinAmount } from '../../utils/rounding-formatting-utils'
import { convertHSBToRGB, scaleBetween } from '../../utils/colours-utils'

const LEVERAGE_WARNING_RANGE = [-4, 4]

const renderColorRangeCell = (value: number, minLvg: number, maxLvg: number): React.ReactNode => {
  const saturation = scaleBetween(Math.abs(value), 40, 100, minLvg, maxLvg)
  const rgb = convertHSBToRGB(0, saturation, 40)
  return (
    <Box
      sx={{
        backgroundColor: `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`,
        width: '100%',
        height: '100%',
        textAlign: 'right',
      }}
    >
      {value}
    </Box>
  )
}

const renderPlainCell = (value: number): React.ReactNode => {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        textAlign: 'right',
      }}
    >
      {value}
    </Box>
  )
}

export const getExposureStatisticColDef = (maxLvg: number, minLvg: number): GridColDef[] => {
  return [
    {
      field: 'owner',
      headerName: 'Owner',
      type: 'string',
      minWidth: 130,
      sortComparator: (v1, v2) => {
        return v1.localeCompare(v2, undefined, { numeric: true })
      }
    },
    {
      field: 'account_type',
      headerName: 'Account Type',
      type: 'string',
      minWidth: 130,
    },
    {
      field: 'bal',
      headerName: 'Balance',
      type: 'number',
      minWidth: 100,
      valueFormatter: (value) => formatRawUsdAmount(value)
    },
    {
      field: 'pos_val',
      headerName: 'Position',
      type: 'number',
      minWidth: 100,
      valueFormatter: (value) => formatRawUsdAmount(value)
    },
    {
      field: 'exp',
      headerName: 'Exposure',
      type: 'number',
      minWidth: 100,
      valueFormatter: (value) => formatRawUsdAmount(value)
    },
    {
      field: 'total_lvg',
      headerName: 'Total Lvg',
      type: 'number',
      minWidth: 50,
      renderCell: (params) => {
        if (params.value <= LEVERAGE_WARNING_RANGE[0] || params.value >= LEVERAGE_WARNING_RANGE[1]) {
          return renderColorRangeCell(params.value, 6, Math.max(Math.abs(minLvg), Math.abs(maxLvg)))
        } else {
          return renderPlainCell(params.value)
        }
      }
    },
    {
      field: 'net_lvg',
      headerName: 'Net Lvg',
      type: 'number',
      minWidth: 50,
      renderCell: (params) => {
        if (params.value <= LEVERAGE_WARNING_RANGE[0] || params.value >= LEVERAGE_WARNING_RANGE[1]) {
          return renderColorRangeCell(params.value, 4, Math.max(Math.abs(minLvg), Math.abs(maxLvg)))
        } else {
          return renderPlainCell(params.value)
        }
      }
    },
    {
      field: 'aval_total_20',
      headerName: 'Avail (20% of Total)',
      type: 'number',
      minWidth: 100,
      valueFormatter: (value) => formatRawUsdAmount(value),
      cellClassName: (params) => {
        return clsx('text', {
          negative: roundCoinAmount(Number(params.value)) < 0
        })
      },
    },
    {
      field: 'aval_plus_20',
      headerName: 'Avail (+20%)',
      type: 'number',
      minWidth: 100,
      valueFormatter: (value) => formatRawUsdAmount(value),
      cellClassName: (params) => {
        return clsx('text', {
          negative: roundCoinAmount(Number(params.value)) < 0
        })
      },
    },
    {
      field: 'aval_minus_20',
      headerName: 'Avail (-20%)',
      type: 'number',
      minWidth: 100,
      valueFormatter: (value) => formatRawUsdAmount(value),
      cellClassName: (params) => {
        return clsx('text', {
          negative: roundCoinAmount(Number(params.value)) < 0
        })
      },
    },
  ]
}

const EXPOSURE_STATISTIC_SORTING: GridSortModel = [
  { field: 'owner', sort: 'asc' },
  { field: 'account_type', sort: 'asc' },
]

export const getExposureStatisticAutosizeOptions = (columns: GridColDef[]): GridAutosizeOptions => {
  return {
    columns: Array.from(columns, item => item?.field),
    includeHeaders: true,
    includeOutliers: true,
  }
}

export const EXPOSURE_STATISTIC_MODE_OPTIONS_CONFIG = {
  endpoint: process.env.REACT_APP_API_EXPOSURE_STATISTIC,
  sortModel: EXPOSURE_STATISTIC_SORTING,
}
